import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    Typography,
    IconButton,
    CircularProgress,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';

// Speech recognition type definitions (copied from ProviderLandingPage)
interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList;
    resultIndex: number;
    error: any;
}

interface SpeechRecognitionResult {
    transcript: string;
    confidence: number;
}

interface SpeechRecognitionResultList {
    [index: number]: SpeechRecognitionResult;
    length: number;
    item(index: number): SpeechRecognitionResult;
}

interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    start(): void;
    stop(): void;
    abort(): void;
    onresult: (event: SpeechRecognitionEvent) => void;
    onerror: (event: SpeechRecognitionEvent) => void;
    onend: () => void;
}

declare global {
    interface Window {
        SpeechRecognition?: new () => SpeechRecognition;
        webkitSpeechRecognition?: new () => SpeechRecognition;
    }
}

interface InsufficientDataModalProps {
    open: boolean;
    missingFactors: string[];
    originalInput: string;
    onSubmit: (augmentedInput: string) => void;
    onClose: () => void;
    isSubmitting: boolean;
}

const InsufficientDataModal: React.FC<InsufficientDataModalProps> = ({
    open,
    missingFactors,
    originalInput,
    onSubmit,
    onClose,
    isSubmitting,
}) => {
    const [inputText, setInputText] = useState(originalInput);
    const [isRecording, setIsRecording] = useState(false);
    const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    // Reset input text when modal opens with new original input
    useEffect(() => {
        if (open) {
            setInputText(originalInput);
        }
    }, [open, originalInput]);

    // Stop recording helper
    const stopRecording = useCallback(() => {
        if (isRecording && recognition) {
            recognition.stop();
            setIsRecording(false);
        }
    }, [isRecording, recognition]);

    // Clean up on unmount or close
    useEffect(() => {
        return () => {
            if (isRecording && recognition) {
                recognition.stop();
            }
        };
    }, [isRecording, recognition]);

    const toggleVoiceRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            try {
                const SpeechRecognitionConstructor =
                    window.SpeechRecognition ||
                    window.webkitSpeechRecognition;

                if (!SpeechRecognitionConstructor) {
                    alert("Speech recognition is not supported in this browser.");
                    return;
                }

                const newRecognition = new SpeechRecognitionConstructor();
                newRecognition.continuous = true;
                newRecognition.interimResults = true;

                newRecognition.onresult = (event: SpeechRecognitionEvent) => {
                    const results = Array.from(event.results);
                    const transcript = results
                        .map(result => {
                            const speechResult = result as unknown as SpeechRecognitionResult[];
                            return speechResult[0]?.transcript || '';
                        })
                        .join('');

                    // Insert at cursor position like landing page
                    if (textAreaRef.current) {
                        const cursorPosition = textAreaRef.current.selectionStart || 0;
                        const currentText = inputText;
                        const newText = currentText.substring(0, cursorPosition) +
                            transcript +
                            currentText.substring(cursorPosition);
                        setInputText(newText);

                        // Move cursor and scroll
                        setTimeout(() => {
                            if (textAreaRef.current) {
                                const newPosition = cursorPosition + transcript.length;
                                textAreaRef.current.selectionStart = newPosition;
                                textAreaRef.current.selectionEnd = newPosition;
                                textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
                            }
                        }, 0);
                    }
                };

                newRecognition.onerror = (event: SpeechRecognitionEvent) => {
                    console.error('Speech recognition error', event.error);
                    setIsRecording(false);
                };

                newRecognition.onend = () => {
                    setIsRecording(false);
                };

                setRecognition(newRecognition);
                newRecognition.start();
                setIsRecording(true);
            } catch (error) {
                console.error('Speech recognition error:', error);
                alert("There was a problem initializing speech recognition.");
            }
        }
    };

    const handleSubmit = () => {
        stopRecording();
        onSubmit(inputText);
    };

    // Format missing factors for display
    const formatMissingFactors = () => {
        const mapFactor = (factor: string) => {
            const f = factor.toLowerCase();
            if (f === 'performance status') return 'performance';
            if (f === 'medical history') return 'history';
            return f;
        };

        if (!missingFactors || missingFactors.length === 0) {
            // Fallback: show factors when backend doesn't provide specific ones
            return 'staging, pathology, biomarkers, presentation, performance, history, diagnostic workup, radiology, molecular results, and/or prior treatment';
        }

        const mappedFactors = missingFactors.map(mapFactor);

        if (mappedFactors.length === 1) return mappedFactors[0];
        if (mappedFactors.length === 2) return `${mappedFactors[0]} and/or ${mappedFactors[1]}`;

        // Show all missing factors
        const displayFactors = [...mappedFactors];
        const lastFactor = displayFactors.pop();
        return `${displayFactors.join(', ')}, and/or ${lastFactor}`;
    };

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            onClose={(_, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
                '& .MuiDialog-paper': {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    p: { xs: 1, sm: 2 },
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '1px solid #e2e8f0',
                    pb: 2,
                    pt: 2,
                    color: '#2c5282',
                    fontWeight: 600,
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                }}
            >
                [Optional] Additional Details
            </DialogTitle>

            <DialogContent sx={{ pt: 4, pb: 2 }}>
                <Typography
                    sx={{
                        color: '#4a5568',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        mb: 3,
                        mt: 2,
                    }}
                >
                    Providing more case details can improve the output quality.
                    Helpful data points may include {formatMissingFactors()}.
                </Typography>

                <Box sx={{ position: 'relative' }}>
                    <TextField
                        inputRef={textAreaRef}
                        fullWidth
                        multiline
                        rows={6}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="Add additional case details here..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#f8fafc',
                                '&:hover fieldset': {
                                    borderColor: '#4299e1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#2c5282',
                                    borderWidth: 2,
                                },
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '0.95rem',
                                lineHeight: 1.5,
                            },
                        }}
                    />

                    {/* Microphone button */}
                    <IconButton
                        onClick={toggleVoiceRecording}
                        disabled={isSubmitting}
                        sx={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            bgcolor: isRecording ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                            color: isRecording ? '#ef4444' : '#4299e1',
                            '&:hover': {
                                bgcolor: isRecording ? 'rgba(239, 68, 68, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                            },
                            '&.Mui-disabled': {
                                color: '#a0aec0',
                            },
                        }}
                    >
                        {isRecording ? <MicOffIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} /> : <MicIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />}
                    </IconButton>
                </Box>

                {isRecording && (
                    <Typography
                        sx={{
                            color: '#e53e3e',
                            fontSize: '0.85rem',
                            mt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#e53e3e',
                                animation: 'blink 1s infinite',
                                '@keyframes blink': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.3 },
                                },
                            }}
                        />
                        Recording... Click the microphone to stop.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2 }}>
                <Button
                    onClick={onClose}
                    disabled={isSubmitting}
                    variant="outlined"
                    sx={{
                        color: '#4a5568',
                        borderColor: '#cbd5e0',
                        px: 3,
                        py: 1,
                        borderRadius: '8px',
                        fontWeight: 500,
                        '&:hover': {
                            borderColor: '#a0aec0',
                            bgcolor: '#f7fafc',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !inputText.trim()}
                    variant="contained"
                    sx={{
                        bgcolor: '#4299e1',
                        color: 'white',
                        px: 4,
                        py: 1,
                        borderRadius: '8px',
                        fontWeight: 500,
                        boxShadow: '0 2px 8px rgba(66, 153, 225, 0.3)',
                        '&:hover': {
                            bgcolor: '#2c5282',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(44, 82, 130, 0.4)',
                        },
                        '&.Mui-disabled': {
                            bgcolor: '#a0aec0',
                            color: 'white',
                        },
                    }}
                >
                    {isSubmitting ? (
                        <>
                            <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                            Submitting...
                        </>
                    ) : (
                        'Submit'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InsufficientDataModal;
